import { compactGraph } from '../../../../services/EpisodeScriptService';
import type { CompactNode } from '../../../../services/EpisodeScriptService';
import type {
  EditRequest,
  EngineId,
  Fixture,
  SidecarMap3,
  StructuralItem,
} from '../../../../dorian-shared/types/episodeScript';
import { IDENTITY_ALIGNMENT } from './lineAlignment';
import type { LineAlignment } from './lineAlignment';

/**
 * One node, flattened for the surface. The flattening itself is the core's — amendment A5 keeps one
 * implementation of it — and this alias is only the surface's name for the shape the preview, the
 * plan panel and the scene lookup read (`CompactNode`, EpisodeScriptService/viewModel/compactGraph).
 */
export type GraphNode = CompactNode;

export { compactGraph };

/** `compactGraph` over a whole fixture, with the null the surface can legitimately hold. */
export function graphOfFixture(fixture: Fixture | null | undefined): GraphNode[] {
  return compactGraph(fixture?.branches ?? []);
}

/**
 * The heading each node has **in the script**, keyed by node id.
 *
 * Round-3 defect 4 was an advice card citing `node2` — the engine's title — where the writer had
 * written a heading. The sidecar map says which line each node's heading is on; the script says what
 * that heading reads as. This function is the one place the two are joined.
 */
export function sceneHeadings(
  map: SidecarMap3 | null | undefined,
  text: string,
  align?: LineAlignment,
): Map<string, string> {
  const headings = new Map<string, string>();
  const lines = String(text ?? '').replace(/\r\n?/g, '\n').split('\n');
  const where = align ?? IDENTITY_ALIGNMENT;
  (map?.blocks ?? []).forEach((block) => {
    if (block.kind !== 'scene') {
      return;
    }
    // T17-04: the map says which block; the script says what it reads as. The join between them is
    // the CURRENT position of that block, not the position it had when the script was decompiled.
    const line = lines[Number(where.current(Number(block.line))) - 1];
    if (typeof line !== 'string') {
      return;
    }
    const matched = line.match(/^\s*==\s*(.*?)\s*$/);
    if (!matched) {
      return;
    }
    const at = matched[1].lastIndexOf(' @ ');
    headings.set(String(block.branchId), (at < 0 ? matched[1] : matched[1].slice(0, at)).trim());
  });

  return headings;
}

/** The scene a node is, as the script spells it, falling back to the graph's own title. */
export function sceneOfBranch(
  headings: Map<string, string>,
  graph: GraphNode[],
  branchId: unknown,
): string | null {
  if (branchId == null) {
    return null;
  }
  const spelled = headings.get(String(branchId));
  if (spelled) {
    return spelled;
  }
  const node = graph.find((item) => String(item.id) === String(branchId));

  return node ? String(node.title).replace(/ \(continuation \d+\)$/, '') : null;
}

/**
 * The node a script line belongs to: the last scene block at or above it (Basil v0.2, P2).
 *
 * It is what lets a margin note offer the protected exit — a note about a name on line 14 can say
 * "Open this scene" only once it knows which scene line 14 is in.
 */
export function branchAtLine(
  map: SidecarMap3 | null | undefined,
  line: number,
  align?: LineAlignment,
): string | null {
  // T17-04: `line` is a line of the script in front of the writer. The map's blocks are addressed
  // in the decompiled text, so the question is asked there — and a line whose origin cannot be
  // established belongs to no scene this function is willing to name.
  const asked = align ? align.baseline(line) : line;

  if (asked == null) {
    return null;
  }
  let found: string | null = null;
  let at = 0;

  (map?.blocks ?? []).forEach((block) => {
    const blockLine = Number(block.line);

    if (block.kind !== 'scene' || !Number.isFinite(blockLine) || blockLine > asked || blockLine < at) {
      return;
    }
    at = blockLine;
    found = block.branchId == null ? null : String(block.branchId);
  });

  return found;
}

/**
 * The writer-facing word for every engine op the compiler can emit (editor.js:350-366). The engine's
 * own spelling never leaves the Advanced footer.
 */
export const CHANGE_WORDS: Readonly<Record<string, string>> = Object.freeze({
  set_step_text: 'a line',
  set_answer_text: 'a choice answer',
  append_step: 'a new line',
  new_scene: 'a new scene',
  scene_metadata: 'the scene heading',
  branch_route: 'where the scene goes next',
  moved_block: 'a line moved to another scene',
  move_step: 'a line moved to another scene',
  missing_identity: "a line Basil can't find any more",
  character_cast: 'who is on screen',
  choice_shape: 'the answers of a choice',
  choice_route_or_price: 'a choice price or destination',
  check: 'a check',
  bookkeeping: 'something the story remembers',
  hub: 'a hub',
  delete_step: 'a line removed',
  insert_step: 'a new line',
  delete_scene: 'a scene removed',
  envelope: 'a setting for this episode',
});

/** The narrowest shape `changePhrase` and `engineWords` read of a request or a structural item. */
export type ChangeLike = {
  op?: string;
  kind?: string;
  branchId?: EngineId | null;
  stepId?: EngineId | null;
  answerId?: EngineId | null;
};

/**
 * What a change with no word of its own reads as (Basil v0.2, P5).
 *
 * "A change Basil has no word for yet" described Basil's vocabulary and sounded broken. This names
 * the limitation instead. It is a whole sentence, so no scene is appended to it.
 */
export const UNNAMED_CHANGE = "Basil can't make this change yet";

/** The change, in the writer's words, with the scene the script spells (editor.js:374-379). */
export function changePhrase(
  item: ChangeLike,
  headings: Map<string, string>,
  graph: GraphNode[],
): string {
  const key = String(item.op ?? item.kind ?? '');
  const words = CHANGE_WORDS[key];

  if (!words) {
    return UNNAMED_CHANGE;
  }
  const scene = sceneOfBranch(headings, graph, item.branchId ?? null);

  return scene ? `${words} in ${scene}` : words;
}

/** The change in the engine's words. This string belongs inside the Advanced footer and nowhere else. */
export function engineWords(item: ChangeLike): string {
  const parts: string[] = [String(item.op ?? item.kind ?? '')];
  if (item.branchId != null) {
    parts.push(`node ${item.branchId}`);
  }
  if (item.stepId != null) {
    parts.push(`step ${item.stepId}`);
  }
  if (item.answerId != null) {
    parts.push(`answer ${item.answerId}`);
  }

  return parts.join(' · ');
}

/** Every change the compiler produced, requests first, in one list. */
export function changeItems(
  requests: EditRequest[] | null | undefined,
  structural: StructuralItem[] | null | undefined,
): ChangeLike[] {
  return [...(requests ?? []), ...(structural ?? [])] as ChangeLike[];
}
